import { NGODatePicker } from "./DatePicker";
import LoadingIn from "../assets/loadingIn.gif";
import downloadMasterIcon from "../assets/icons/download-master.svg";
import closeIcon from "../images/icons/Icon2.png";
import { useEffect, useState } from "react";
import LoadingGIF from "../assets/loadingIn.gif";
import Select from "react-select";
import {
  calculateTextWidth,
  formatDateByTimezone,
  getCultureFromLs,
} from "../utils/commonFunctions";
import { APIEndpoint } from "../utils/axios";
import moment from "moment";
import { toast } from "react-toastify";
import checkDownloadStatus from "../utils/checkDownloadStatus";

export const RetrievalVarianceReportDownload = (props) => {
  const [cateringGroupOptions, setCateringGroupOptions] = useState([]);
  const [isCatLoading, setIsCatLoading] = useState(false);
  const [reportDate, setReportDate] = useState(new Date());
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedCateringGroup, setSelectedCateringGroup] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isWarehousesLoading, setIsWarehousesLoading] = useState(false);
  const [cateringGroupWidth, setCateringGroupWidth] = useState("17rem");
  const[submissionError, setSubmissionError] = useState(null);

  const getCustomStyles = (minWidth = "17rem") => ({
    input: (defaultStyles) => ({
      ...defaultStyles,
      color: "#fff",
    }),
    control: (defaultStyles, state) => ({
      ...defaultStyles,
      width: minWidth,
      minWidth: minWidth,
      backgroundColor: '#0f172a',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer', 
    }),
    option: (defaultStyles) => ({
      ...defaultStyles,
      textAlign: "left",
    }),
    placeholder: (defaultStyles, state) => ({
      ...defaultStyles,
      textAlign: "left",
      color: state.isFocused ? "#6e7c84" : "#fff",
    }),
    singleValue: (defaultStyles) => ({
      ...defaultStyles,
      textAlign: "left",
      color: "#fff",
    }),
    indicatorSeparator: (provided) => ({ ...provided, display: "none" }),
  });

  const onDateChangeRep = (e) => {
    setSelectedWarehouse(null);
    setSelectedCateringGroup(null);
    setCateringGroupOptions([]);
    setWarehouses([]);
    const { value } = e.target;
    setReportDate(value);
    loadCateringGroupsByDate(value);
  };

  useEffect(() => {
    if (!reportDate) return;
    loadCateringGroupsByDate(reportDate);
  }, [reportDate]);

  useEffect(() => {
    if (!selectedCateringGroup?.value) return;

    const fetchWarehouses = async () => {
      setIsWarehousesLoading(true);
      const selectedCateringGroupDetails = cateringGroupOptions.find(
        (cg) => cg.value === selectedCateringGroup?.value
      );
      const availableDestinations =
        selectedCateringGroupDetails?.locations?.map(
          (location) => location.store.destination
        );

      if (!availableDestinations || availableDestinations.length === 0) {
        setWarehouses([]);
        setSelectedWarehouse(null);
        return;
      }

      try {
        // Get the LAST destination from the original array (before deduplication)
        const lastDestination =
          availableDestinations[availableDestinations.length - 1];

        // Get unique destinations
        const uniqueDestinations = [...new Set(availableDestinations)];

        // Fetch all warehouses in parallel and wait for all to complete
        const allResponses = await Promise.all(
          uniqueDestinations.map(
            (destination) =>
              APIEndpoint.get(`airports/${destination}/warehouses`).then(
                (resp) => ({ destination, data: resp })
              ) // Keep track of which destination
          )
        );

        // Collect all warehouse options from all responses
        const warehouseOptions = [];
        const seenWarehouseIds = new Set();
        let firstWarehouseFromLastDestination = null;

        allResponses.forEach((respObj) => {
          respObj.data.items.forEach((item, index) => {
            // Check if this is the first warehouse from the last destination
            if (respObj.destination === lastDestination && index === 0) {
              firstWarehouseFromLastDestination = item.warehouseId;
            }

            // Avoid duplicates if same warehouse appears in multiple destinations
            if (!seenWarehouseIds.has(item.warehouseId)) {
              seenWarehouseIds.add(item.warehouseId);
              warehouseOptions.push({
                key: item.warehouseId,
                label: item.warehouseName,
                value: item.warehouseId,
                destination: respObj.destination,
              });
            }
          });
        });
        setIsWarehousesLoading(false);
        // Set warehouses first
        setWarehouses(warehouseOptions);

        // Set the first warehouse from the last destination as selected
        if (firstWarehouseFromLastDestination) {
          setSelectedWarehouse({
            key: firstWarehouseFromLastDestination,
            value: firstWarehouseFromLastDestination,
            destination: lastDestination,
          });
        } else if (warehouseOptions.length > 0) {
          // Fallback: if last destination has no warehouses, use first available
          setSelectedWarehouse({
            key: warehouseOptions[0].value,
            value: warehouseOptions[0].value,
            destination: warehouseOptions[0].destination,
          });
        }
      } catch (error) {
        setWarehouses([]);
        setSelectedWarehouse(null);
      } finally {
        setIsWarehousesLoading(false);
      }
    };

    fetchWarehouses();
  }, [selectedCateringGroup, cateringGroupOptions]);

  async function loadCateringGroupsByDate(value) {
    setIsCatLoading(true);
    try {
      const requestPayload = {
        date: formatDateByTimezone(new Date(value ?? new Date()), false),
      };

      /** Get catering groups based on the selected date  */
      const response = await APIEndpoint.post(
        `operations/rotations/find`,
        requestPayload
      );

      let cateringGroupOptns = [];
      response.rotations.forEach((ele) => {
        const dte = moment(ele.legs[0]?.std)
          .tz(getCultureFromLs()?.timezone.name)
          .format("DD-MM-YYYY hh:mm A");
        const locationOptions = ele.legs.map((leg) => ({
          key: leg.id,
          label: leg.origin,
          value: leg.origin,
          cateringGroup: { id: ele.id, name: ele.name },
          store: {
            flightNumber: leg.flightNumber,
            origin: leg.origin,
            destination: leg.destination,
          },
        }));

        cateringGroupOptns.push({
          key: ele.id,
          label: `${ele.tailNumber}/${ele.flightPath}/${dte}`,
          value: ele.id,
          locations: locationOptions,
        });
      });

      setCateringGroupOptions(cateringGroupOptns);
    } catch (error) {
      toast.error("Failed to load Catering Groups");
    } finally {
      setIsCatLoading(false);
    }
  }

  const onWarehouseChange = (e) => {
    if (!e) return setSelectedWarehouse(null);
    setSelectedWarehouse(e);
  };

  const onCateringGroupChange = (e) => {
    if (!e) {
      setSelectedCateringGroup(null);
      setSelectedWarehouse(null);
      setWarehouses([]);
      setCateringGroupWidth("17rem");
      return;
    }

    if (e?.label) {
      const newWidth = calculateTextWidth(e.label);
      setCateringGroupWidth(`${newWidth + 100}px`);
    } else {
      setCateringGroupWidth("17rem");
    }

    setSelectedCateringGroup(e);
  };

  const onDownloadSuccess = () => {
    props.onClose();
  };

  const onDownloadFailure = (error) => {
    props.onClose();
    toast.error("Failed to download retrieval variance report");
  };

  const onDownloadClick = async () => {
    if(!selectedCateringGroup?.value){
      setSubmissionError("Select the catering group!");
      setTimeout(() => {
        setSubmissionError(null);
      }, 3000);
      return;
    }
    if(!selectedWarehouse){
      setSubmissionError("Select the warehouse!");
      setTimeout(() => {
        setSubmissionError(null);
      }, 3000);
      return;
    }

    // here last leg with the matching destination is considered as the store
    const store = selectedCateringGroup?.locations
      ?.filter((location) => location.store.destination === selectedWarehouse?.destination)
      ?.pop()?.store;

    const requestPayload = {
      date: formatDateByTimezone(new Date(reportDate), false),
      warehouseIds: [selectedWarehouse.value],
      cateringGroup: {
        id: selectedCateringGroup?.value,
        name: selectedCateringGroup?.label,
      },
      reportType: "retrievalVarianceReport",
      store: store,
    };

    let header = {
      Accept: "text/csv",
    };
    try {
      setIsDownloading(true);
      const res = await APIEndpoint.post(
        `sales/reports`,
        requestPayload,
        header
      );
      if (res.status === "scheduled") {
        checkDownloadStatus(res.id, onDownloadSuccess, onDownloadFailure);
      }
    } catch (error) {
      toast.error("Failed to download retrieval variance report");
      props.onClose();
    } finally {
      setIsDownloading(false);
    }
  };

  const { onClose } = props;

  return (
    <div className="overlay" role="dialog">
      <div className="dialog sm-scrollbar">
        <div className="dialog__content">
            <div className='dialog-header flexRow alignItemsCenter width100 content-tabs'>
                    <h2 className="dialog__title subheading-text-medium fontMedium justifyContentCenter alignItemsCenter margBottom0 width50">Download Retrieval Variance Report</h2>
                        <button className='close-btn-ht' onClick={onClose}>
                          <img src={closeIcon} alt="close" data-icon='close'/>
                        </button>
                  </div>

          <hr />

          <div className="dialog__body_container flexRow mt-4 button-align justifyContentCenter flexWrap report-font">
            <div className={`dialog__container_item input-container`}>
              <div className={`dialog__description`}>
                <div className={`flexCol`}>
                  <label className={`textAlignLeft fontRegular req`}>
                    Date
                  </label>
                  <NGODatePicker
                    style={{ minWidth: cateringGroupWidth, textAlign: "left" }}
                    isclearable={true}
                    value={reportDate}
                    placeholder={"Select Date"}
                    placeholdertext={"Select Date"}
                    onChange={onDateChangeRep}
                    className="edit-input-full-width"
                  />
                </div>

                <>
                  <div className="flexCol">
                    <div className="flexRow justifyContentFlexStart">
                      <label
                        style={{ marginBottom: "0.9rem" }}
                        htmlFor="cateringGroup"
                        className="req textAlignLeft"
                      >
                        Catering Group
                      </label>
                      {isCatLoading ? (
                        <img
                          src={LoadingGIF}
                          style={{ width: "16px", height: "16px" }}
                          alt="Loading"
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                    <Select
                      inputId="cateringGroup"
                      isClearable
                      isDisabled={false}
                      isSearchable
                      options={cateringGroupOptions}
                      placeholder="Select Catering Group"
                      value={cateringGroupOptions.filter(
                        (cg) => cg.value === selectedCateringGroup?.value
                      )}
                      onChange={onCateringGroupChange}
                      styles={getCustomStyles(cateringGroupWidth)}
                    />
                  </div>
                </>
                <>
                  <div className="flexRow justifyContentFlexStart">
                    <label
                      style={{ marginBottom: "0.9rem", marginTop: "1rem" }}
                      htmlFor="warehouse"
                      className={`req textAlignLeft`}
                    >
                      Select Warehouse
                    </label>
                    {isWarehousesLoading ? (
                      <img
                        src={LoadingGIF}
                        style={{ width: "16px", height: "16px", marginTop: "1rem" }}
                        alt="Loading"
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                  <Select
                    options={warehouses}
                    value={warehouses?.filter(
                      (it) => it.value === selectedWarehouse?.value
                    )}
                    onChange={onWarehouseChange}
                    styles={getCustomStyles(cateringGroupWidth)}
                    placeholder="Select Warehouse"
                    isSearchable={false}
                    isClearable={true}
                  />
                </>

                <button
                  type="button"
                  onClick={onDownloadClick}
                  className="btn btn-info mt-4"
                  style={{ color: "white", width: "100%" }}
                >
                  {isDownloading ? (
                    <div className="flexRow justifyContentCenter alignItemsCenter">
                      <img
                        src={LoadingIn}
                        style={{
                          marginRight: "5px",
                          width: "25px",
                          height: "25px",
                        }}
                        alt="Loading"
                      />{" "}
                      Processing...
                    </div>
                  ) : (
                    <span>
                      {" "}
                      <img
                        className="icon-size mt-1"
                        alt="downloadMaster"
                        src={downloadMasterIcon}
                      />
                      Download
                    </span>
                  )}
                </button>

              
              </div>
              {submissionError && (
                  <div className='flexRow justifyContentCenter alignItemsCenter textAlignLeft' style={{marginTop:"1.5rem",width:"100%"}}>
                    <p style={{width:"100%"}} className='listErrorMessagesCls'>{submissionError}</p>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
